import {
  GetMeinOrtPostsSortByEnum,
  GetMeinOrtPostsTypesEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo } from 'react';

import { useGetMeinOrtPosts } from '@/features/api/hooks/grapevine-post/useGetMeinOrtPosts';
import { useAppState } from '@/features/authorization/provider/useAppState';
import {
  EVENTS_TEASER_COUNT,
  MARKT_OFFER_TEASER_COUNT,
  MARKT_SEEKING_TEASER_COUNT,
  NEWS_TEASER_COUNT,
  PLAUSCH_TEASER_COUNT
} from '@/features/mein-ort/utils/groupPostsByChannel';
import {
  LAST_100_DAYS,
  TODAY_AS_NUMBER
} from '@/features/posts/constants/params';
import { usePostSortStore } from '@/features/posts/store/postSortStore';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { countNewPosts } from '@/features/posts/utils/newPosts';
import { useBadgeStore } from '@/shared/store/useBadgeStore';

export type MeinOrtChannelTeasersEntry = {
  posts: GenericPostDetail[] | undefined;
  hasNew: boolean;
};

export type MeinOrtChannelTeasers = {
  news: MeinOrtChannelTeasersEntry;
  plausch: MeinOrtChannelTeasersEntry;
  markt: MeinOrtChannelTeasersEntry;
  events: MeinOrtChannelTeasersEntry;
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  refetch: () => Promise<unknown>;
};

export const useMeinOrtChannelTeasers = (
  enabled: boolean
): MeinOrtChannelTeasers => {
  const sort = usePostSortStore((state) => state.sort);

  const newsQuery = useGetMeinOrtPosts(
    {
      types: new Set([GetMeinOrtPostsTypesEnum.NewsItem]),
      count: NEWS_TEASER_COUNT,
      startTime: LAST_100_DAYS,
      sortBy: sort
    },
    enabled
  );
  const plauschQuery = useGetMeinOrtPosts(
    {
      types: new Set([GetMeinOrtPostsTypesEnum.Gossip]),
      count: PLAUSCH_TEASER_COUNT,
      startTime: LAST_100_DAYS,
      sortBy: sort
    },
    enabled
  );
  const marktQuery = useGetMeinOrtPosts(
    {
      types: new Set([
        GetMeinOrtPostsTypesEnum.Offer,
        GetMeinOrtPostsTypesEnum.Seeking
      ]),
      count: MARKT_OFFER_TEASER_COUNT + MARKT_SEEKING_TEASER_COUNT,
      startTime: LAST_100_DAYS,
      sortBy: sort
    },
    enabled
  );
  const eventsQuery = useGetMeinOrtPosts(
    {
      types: new Set([GetMeinOrtPostsTypesEnum.Happening]),
      count: EVENTS_TEASER_COUNT,
      startTime: TODAY_AS_NUMBER,
      sortBy: GetMeinOrtPostsSortByEnum.HappeningStart
    },
    enabled
  );

  const readPostBadgeEntries = useBadgeStore(
    (state) => state.readPostBadgeEntries
  );
  const { userId } = useAppState();

  return useMemo(() => {
    const news = newsQuery.data ?? [];
    const plausch = plauschQuery.data ?? [];
    const markt = marktQuery.data ?? [];
    const events = eventsQuery.data ?? [];

    const hasNew = (posts: GenericPostDetail[]) =>
      countNewPosts(posts, readPostBadgeEntries, userId) > 0;

    const refetch = async () => {
      await Promise.all([
        newsQuery.refetch(),
        plauschQuery.refetch(),
        marktQuery.refetch(),
        eventsQuery.refetch()
      ]);
    };

    return {
      news: { posts: news, hasNew: hasNew(news) },
      plausch: { posts: plausch, hasNew: hasNew(plausch) },
      markt: { posts: markt, hasNew: hasNew(markt) },
      events: { posts: events, hasNew: hasNew(events) },
      isLoading:
        newsQuery.isLoading ||
        plauschQuery.isLoading ||
        marktQuery.isLoading ||
        eventsQuery.isLoading,
      isError:
        newsQuery.isError ||
        plauschQuery.isError ||
        marktQuery.isError ||
        eventsQuery.isError,
      isRefetching:
        newsQuery.isRefetching ||
        plauschQuery.isRefetching ||
        marktQuery.isRefetching ||
        eventsQuery.isRefetching,
      refetch
    };
  }, [
    newsQuery,
    plauschQuery,
    marktQuery,
    eventsQuery,
    readPostBadgeEntries,
    userId
  ]);
};
